/**
 * PaneRouter component that provides routing within a specific pane
 */

import { PaneRouterContext } from "@/context/PaneRouterContext";
import { useAppNavigate, usePaneLocation } from "@/hooks/pane-routing";
import type {
  PaneLocation,
  PaneNavigateOptions,
  PaneRouterContextValue,
  PaneRouterProps,
} from "@/types/pane-routing";
import React, { useMemo } from "react";

/**
 * PaneRouter provides routing context for a specific pane
 */
export const PaneRouter: React.FC<PaneRouterProps> = ({
  pane,
  children,
  fallback = null,
}) => {
  const paneLocation = usePaneLocation(pane);
  const appNavigate = useAppNavigate();

  // Create default location if pane location doesn't exist
  const location: PaneLocation = useMemo(() => {
    return (
      paneLocation || {
        pathname: "/",
        search: "",
        hash: "",
        state: null,
        key: `pane-${pane}-default`,
      }
    );
  }, [paneLocation, pane]);

  // Create pane-specific navigate function
  const navigate = useMemo(() => {
    return (to: string, options?: PaneNavigateOptions) => {
      appNavigate({ pane, to, options });
    };
  }, [appNavigate, pane]);

  // Context value
  const contextValue: PaneRouterContextValue = useMemo(
    () => ({
      pane,
      location,
      navigate,
      params: {}, // Will be populated by PaneRoute components
    }),
    [pane, location, navigate],
  );

  // If no pane location exists and no fallback, don't render anything
  if (!paneLocation && !fallback) {
    return null;
  }

  // If no pane location exists but fallback is provided, show fallback
  if (!paneLocation && fallback) {
    return (
      <PaneRouterContext.Provider value={contextValue}>
        {fallback}
      </PaneRouterContext.Provider>
    );
  }

  return (
    <PaneRouterContext.Provider value={contextValue}>
      {children}
    </PaneRouterContext.Provider>
  );
};
